import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getScreenSeat, clearScreenSeat } from '../utils/sessionManager';
import { processPayment } from '../services/api';
import { FaSpinner, FaTimes, FaCheck } from 'react-icons/fa';
import { useOrder } from '../context/OrderContext';
import { usePayment } from '../context/PaymentContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const screens = [
    { id: 'screen1', name: 'Screen 1' },
    { id: 'screen2', name: 'Screen 2' },
    { id: 'screen3', name: 'Screen 3' },
    { id: 'screen4', name: 'Screen 4' },
    { id: 'screen5', name: 'Screen 5' },
    { id: 'screen6', name: 'Screen 6' },
  ];

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderContext = useOrder();
  const { paymentData, hasValidPaymentData, clearPaymentData } = usePayment();
  const orderItems = paymentData?.orderItems || [];
  const userSessionId = paymentData?.userSessionId || '';
  
  const [formData, setFormData] = useState({
    name: '',
    seatNumber: '',
    screen: '',
    email: '',
    mpesaNumber: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Load screen and seat from session
  useEffect(() => {
    console.log('PaymentPage useEffect running - checking for session data');
    
    // Check localStorage directly first
    // eslint-disable-next-line no-unused-vars
    const rawData = localStorage.getItem('screen_seat_data');
    console.log('Raw localStorage data:', rawData);
    
    const screenSeatData = getScreenSeat();
    console.log('Processed session data from getScreenSeat():', screenSeatData);
    
    if (screenSeatData) {
      console.log('Setting form data with screen:', screenSeatData.screen, 'and seat:', screenSeatData.seat);
      
      // Use the screen ID directly for the dropdown
      setFormData(prev => {
        const newFormData = {
          ...prev,
          screen: screenSeatData.screen, // Use screen ID, not name
          seatNumber: screenSeatData.seat
        };
        //console.log('Updated form data:', newFormData);
        return newFormData;
      });
    } else {
     // console.log('No session data found or data has expired');
    }
  }, []); // No dependencies needed since screens is defined outside the component
  // Simplified state management - we'll use the browser alert for success cases
  // Order data is already available from the orderContext

  // Removed duplicate SCREENS definition as it's already defined at the top of the file

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    // Validate seat number
    if (!formData.seatNumber.trim()) {
      newErrors.seatNumber = 'Seat number is required';
    }
    
    // Validate screen
    if (!formData.screen) {
      newErrors.screen = 'Please select a screen';
    }
    
    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Validate mpesa number
    if (!formData.mpesaNumber.trim()) {
      newErrors.mpesaNumber = 'M-Pesa number is required';
    } else if (!/^(07|01)\d{8}$/.test(formData.mpesaNumber)) {
      newErrors.mpesaNumber = 'Please enter a valid 10-digit M-Pesa number starting with 07 or 01';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Show confirmation modal instead of submitting directly
  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Show confirmation modal
    setShowConfirmModal(true);
  };
  
  // Handle actual payment submission after confirmation
  const handleConfirmedSubmit = async () => {
    // Close the modal
    setShowConfirmModal(false);
    
    // Clear screen and seat session after successful form submission
    clearScreenSeat();
    
    setLoading(true);
    setSubmitError('');
    
    // Process the payment
    const response = await processPayment(formData, orderItems, userSessionId);
    console.log('Payment processed:', response);
    console.log('Response status:', response?.payment_status);
    
    // Handle success case
    if (response && response.payment_status === 201) {
      // Clear all order data after successful payment
      if (orderContext && typeof orderContext.clearOrder === 'function') {
        orderContext.clearOrder();
      }
      // Also clear payment data
      clearPaymentData();
      
      // Success is handled via window.alert in the API service
      // We just need to reset the form here
      setFormData({
        name: '',
        seatNumber: '',
        screen: '',
        email: '',
        mpesaNumber: ''
      });
      setSubmitSuccess(true);
    } else {
      // For failures, show the error message in the form
      setSubmitError(response.message || 'An error occurred while processing your payment');
    }
    
    setLoading(false);
  };
  
  // Cancel confirmation and close modal
  const handleCancelConfirmation = () => {
    setShowConfirmModal(false);
  };

  // These functions were removed as they are no longer needed after removing the order summary

  // Remove the old success screen entirely as we're using the modal instead

  return (
    <>
      <Header searchQuery={''} setSearchQuery={() => {}} toggleCart={null} />
      <div className="payment-page">
        <div className="payment-container">
          <h1>Pay with M-PESA</h1>
          
          {/* Confirmation Modal */}
          {showConfirmModal && (
            <div className="confirmation-modal-overlay">
              <div className="confirmation-modal">
                <div className="confirmation-modal-header">
                  <h2>Confirm Payment Details</h2>
                  <button 
                    className="close-modal-btn" 
                    onClick={handleCancelConfirmation}
                  >
                    <FaTimes />
                  </button>
                </div>
                
                <div className="confirmation-modal-body">
                  <p>Please verify your payment details:</p>
                  
                  <div className="confirmation-details">
                    <div className="confirmation-item">
                      <span className="label">Name:</span>
                      <span className="value">{formData.name}</span>
                    </div>
                    
                    <div className="confirmation-item">
                      <span className="label">Screen:</span>
                      <span className="value">
                        {screens.find(s => s.id === formData.screen)?.name || formData.screen}
                      </span>
                    </div>
                    
                    <div className="confirmation-item">
                      <span className="label">Seat Number:</span>
                      <span className="value">{formData.seatNumber}</span>
                    </div>
                    
                    <div className="confirmation-item">
                      <span className="label">Email:</span>
                      <span className="value">{formData.email}</span>
                    </div>
                    
                    <div className="confirmation-item">
                      <span className="label">M-Pesa Number:</span>
                      <span className="value">{formData.mpesaNumber}</span>
                    </div>
                  </div>
                </div>
                
                <div className="confirmation-modal-footer">
                  <button 
                    className="cancel-btn" 
                    onClick={handleCancelConfirmation}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    className="confirm-btn" 
                    onClick={handleConfirmedSubmit}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="spinner" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaCheck /> Confirm Payment
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <div className="payment-form-container">
            <form onSubmit={handleFormSubmit} className="payment-form">
              {submitError && (
                <div className="form-error">
                  <p>{submitError}</p>
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="name">First Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your first name"
                  disabled={loading}
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="seatNumber">Seat Number</label>
                <input
                  type="text"
                  id="seatNumber"
                  name="seatNumber"
                  value={formData.seatNumber}
                  onChange={handleChange}
                  placeholder="Enter your seat number"
                  disabled={loading}
                />
                {errors.seatNumber && <span className="error-message">{errors.seatNumber}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="screen">Screen</label>
                <select
                  id="screen"
                  name="screen"
                  value={formData.screen}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">Select a screen</option>
                  {screens.map(screen => (
                    <option key={screen.id} value={screen.id}>{screen.name}</option>
                  ))}
                </select>
                {errors.screen && <span className="error-message">{errors.screen}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  disabled={loading}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="mpesaNumber">M-Pesa Number</label>
                <input
                  type="text"
                  id="mpesaNumber"
                  name="mpesaNumber"
                  value={formData.mpesaNumber}
                  onChange={handleChange}
                  placeholder="Enter your M-Pesa number (e.g., 0712345678)"
                  disabled={loading}
                />
                {errors.mpesaNumber && <span className="error-message">{errors.mpesaNumber}</span>}
              </div>
              
              <button 
                type="submit" 
                className="submit-payment-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FaSpinner className="spinner" />
                    Processing...
                  </>
                ) : (
                  'Complete Payment'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PaymentPage;
